import React, { useState, useEffect } from 'react'
import { CookbookStep, CookbookQuestionnaire, EMPTY_COOKBOOK_QUESTIONNAIRE } from '../../types'
import RecipeLandingPage from './RecipeLandingPage'
import RecipeConsentStep from './RecipeConsentStep'
import RecipeQuestionnaireStep from './RecipeQuestionnaireStep'
import RecipeSubmittedStep from './RecipeSubmittedStep'
import RecipeHeader from './RecipeHeader'

export default function RecipeApp() {
  const [step, setStep] = useState<CookbookStep>('landing')

  // Set the page title for the recipes section
  useEffect(() => {
    document.title = 'Family Cookbook | Koru Kids'
  }, [])
  const [questionnaire, setQuestionnaire] = useState<CookbookQuestionnaire>(EMPTY_COOKBOOK_QUESTIONNAIRE)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string>('')

  const handleGetStarted = () => {
    setStep('consent')
  }

  const handleConsentAccepted = () => {
    setStep('questionnaire')
  }

  const handleQuestionnaireChange = (updates: Partial<CookbookQuestionnaire>) => {
    setQuestionnaire(prev => ({ ...prev, ...updates }))
  }

  const handleSubmit = async () => {
    if (!questionnaire.email) {
      setError('Please enter your email address')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch('/api/cookbooks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken()
        },
        body: JSON.stringify(questionnaire)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to submit questionnaire')
      }

      setSubmittedEmail(questionnaire.email)
      setStep('submitted')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartOver = () => {
    setQuestionnaire(EMPTY_COOKBOOK_QUESTIONNAIRE)
    setError(null)
    setSubmittedEmail('')
    setStep('landing')
  }

  // Landing page has its own layout
  if (step === 'landing') {
    return <RecipeLandingPage onGetStarted={handleGetStarted} />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <RecipeHeader />
      <main className="max-w-4xl mx-auto px-4 py-8">
        {step === 'consent' && (
          <RecipeConsentStep onAccept={handleConsentAccepted} />
        )}

        {step === 'questionnaire' && (
          <RecipeQuestionnaireStep
            questionnaire={questionnaire}
            onChange={handleQuestionnaireChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={error}
          />
        )}

        {step === 'submitted' && (
          <RecipeSubmittedStep
            email={submittedEmail}
            onStartOver={handleStartOver}
          />
        )}
      </main>
    </div>
  )
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}
